import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { useAuthStore } from "@/stores/authStore";
import { Login } from "@/pages/Login";
import { Register } from "@/pages/Register";
import { ForgotPassword } from "@/pages/ForgotPassword";
import { ResetPassword } from "@/pages/ResetPassword";
import { Home } from "@/pages/Home";
import { BuilderSettings } from "@/pages/BuilderSettings";
import { AdminDashboard } from "@/pages/AdminDashboard";
import Configurator from "@/pages/Configurator";
import type { ReactNode } from "react";

const queryClient = new QueryClient();

function RequireAuth({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const profileSetupRequired = useAuthStore(
    (s) => s.profileSetupRequired,
  );
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "builder" && profileSetupRequired)
    return <Navigate to="/settings" replace />;
  return children;
}

function RequireBuilder({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "builder") return <Navigate to="/home" replace />;
  return children;
}

function RedirectIfAuth() {
  const user = useAuthStore((s) => s.user);
  const profileSetupRequired = useAuthStore(
    (s) => s.profileSetupRequired,
  );
  if (user) {
    if (user.role === "builder" && profileSetupRequired)
      return <Navigate to="/settings" replace />;
    return <Navigate to="/home" replace />;
  }
  return <Login />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<RedirectIfAuth />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/home"
            element={
              <RequireAuth>
                <Home />
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireBuilder>
                <BuilderSettings />
              </RequireBuilder>
            }
          />
          <Route
            path="/surveys/new"
            element={
              <RequireAuth>
                <Configurator />
              </RequireAuth>
            }
          />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </QueryClientProvider>
  );
}
